import React, { useEffect, type RefObject } from 'react';
import {
	createActionsHook,
	createContainer,
	createStateHook,
	createStore,
	type Action,
} from '@atlassian/react-sweet-state';
import type { State, TableContainerProps } from './types';

export const TableStateContainer = createContainer();

export const INITIAL_TABLE_STATE: State = {
	height: 0,
	width: 0,
	scrollHeight: 0,
	scrollWidth: 0,
};

const actions = {
	update:
		(newState: Partial<State>): Action<State> =>
		({ setState, getState }) => {
			const state = getState();
			setState({
				...state,
				...newState,
			});
		},
} as const;

type Actions = typeof actions;

const Store = createStore<State, Actions>({
	containedBy: TableStateContainer,
	initialState: INITIAL_TABLE_STATE,
	name: 'TableStateManager',
	actions,
});

const useTableStateActions = createActionsHook(Store);

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
export const useTableStateSelector = createStateHook(Store, {
	selector: (state, cb: Function) => cb(state),
}) as unknown as <T>(cb: (state: State) => T) => T;

const useTableSize = (elementRef: RefObject<HTMLElement>) => {
	const { update } = useTableStateActions();

	useEffect(() => {
		const element = elementRef.current;

		if (element) {
			const resizeObserver = new ResizeObserver(() => {
				update({
					height: element.clientHeight,
					width: element.clientWidth,
					scrollHeight: element.scrollHeight,
					scrollWidth: element.scrollWidth,
				});
			});

			resizeObserver.observe(element);

			return () => {
				resizeObserver.disconnect();
			};
		}
	}, [elementRef, update]);
};

const TableStateProviderInner = ({ tableRef, children }: TableContainerProps) => {
	useTableSize(tableRef);
	return <>{children}</>;
};

/**
 * Provides a scroll element via context to this sub-tree. This is required by
 * `useVirtual`.
 */
export const TableStateProvider = (props: TableContainerProps) => {
	return (
		<TableStateContainer>
			<TableStateProviderInner {...props} />
		</TableStateContainer>
	);
};
